import { useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { wordpressService } from '../../services/wordpress';
import Spinner from '../Spinner';

// Redirect from /post/id/123 to /post/post-slug
export default function PostRedirect({ className = '' }) {
  const { id } = useParams();

  if (!id || isNaN(Number(id))) {
    return <Navigate to="/posts" replace />;
  }

  return (
    <PostIdRedirect 
      postId={Number(id)} 
      className={className}
    />
  );
}

function PostIdRedirect({ postId, className = '' }) {
  useEffect(() => {
    const redirectToSlug = async () => {
      try {
        const post = await wordpressService.getPostById(postId);
        if (post) {
          // Use replace to redirect to slug-based URL
          window.location.replace(`/post/${post.slug}`);
        } else {
          // Post not found, redirect to 404
          window.location.replace('/posts');
        }
      } catch (error) {
        console.error('Error fetching post for redirect:', error);
        // On error, redirect to posts listing
        window.location.replace('/posts');
      }
    };

    redirectToSlug();
  }, [postId]);

  // Show loading state while redirecting
  return (
    <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${className}`}>
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Spinner />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Redirecting...</h2>
        <p className="text-gray-600">
          Taking you to the updated URL for this post.
        </p>
      </div>
    </div>
  );
}

// Bulk redirect utility for migrating multiple URLs
export function BulkRedirectUtility({ idToSlugMap, onComplete }) {
  useEffect(() => {
    const setupRedirects = () => {
      // Create redirect rules for old URLs
  const redirectRules = [];
      
      idToSlugMap.forEach((slug, id) => {
        redirectRules.push({
          from: `/post/id/${id}`,
          to: `/post/${slug}`
        });
        redirectRules.push({
          from: `/post/${id}`,
          to: `/post/${slug}`
        });
      });

      // Store redirect rules in localStorage for client-side routing
      localStorage.setItem('post_redirects', JSON.stringify(redirectRules));
      
      if (onComplete) {
        onComplete();
      }
    };

    setupRedirects();
  }, [idToSlugMap, onComplete]);

  return null; // This component doesn't render anything
}

// Hook for handling redirects in routing
export function usePostRedirects() {
  const getRedirectUrl = (currentPath) => {
    try {
      const redirectRules = localStorage.getItem('post_redirects');
      if (!redirectRules) return null;

  const rules = JSON.parse(redirectRules);
      const rule = rules.find(r => r.from === currentPath);
      
      return rule ? rule.to : null;
    } catch (error) {
      console.error('Error reading redirect rules:', error);
      return null;
    }
  };

  return { getRedirectUrl };
}

// Component for handling legacy WordPress URLs
export function LegacyUrlRedirect({ className = '' }) {
  useEffect(() => {
    const handleLegacyUrls = () => {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      
      // Handle different legacy URL patterns
      const patterns = [
        // WordPress default permalink: /?p=123
        { 
          pattern: /^\?p=(\d+)$/,
          transform: (matches) => `/post/id/${matches[1]}`
        },
        // WordPress post type URLs: /posts/123
        {
          pattern: /^\/posts\/(\d+)\/?$/,
          transform: (matches) => `/post/id/${matches[1]}`
        },
        // Other legacy patterns can be added here
      ];

      for (const { pattern, transform } of patterns) {
        const matches = url.search.match(pattern) || url.pathname.match(pattern);
        if (matches) {
          const newPath = transform(matches);
          window.location.replace(newPath);
          return;
        }
      }
    };

    handleLegacyUrls();
  }, []);

  return (
    <div className={`min-h-screen bg-gray-50 flex items-center justify-center ${className}`}>
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <Spinner />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Processing URL...</h2>
        <p className="text-gray-600">
          Converting legacy URL format.
        </p>
      </div>
    </div>
  );
}

// Utility function to generate redirect map from WordPress posts
export async function generateRedirectMap() {
  const redirectMap = new Map();
  
  try {
    let page = 1;
    let hasMore = true;
    
    while (hasMore) {
      const response = await wordpressService.getPosts({
        page,
        per_page: 100, // Fetch in batches
        _embed: false // Don't need embedded data for redirects
      });
      
      response.posts.forEach(post => {
        redirectMap.set(post.id, post.slug);
      });
      
      hasMore = response.hasMore;
      page++;
    }
  } catch (error) {
    console.error('Error generating redirect map:', error);
  }
  
  return redirectMap;
}

// Component to preload redirect data
export function RedirectMapLoader({ onLoaded, onError }) {
  useEffect(() => {
    const loadRedirectMap = async () => {
      try {
        const map = await generateRedirectMap();
        if (onLoaded) {
          onLoaded(map);
        }
      } catch (error) {
        console.error('Failed to load redirect map:', error);
        if (onError) {
          onError(error);
        }
      }
    };

    // Check if we already have redirect data in localStorage
    const existingData = localStorage.getItem('post_redirects');
    if (!existingData) {
      loadRedirectMap();
    }
  }, [onLoaded, onError]);

  return null;
} 